import { useTranslation } from 'react-i18next'
import { ReportChart } from '@/components/relatorios/ReportChart'
import { ReportKpiCard } from '@/components/relatorios/ReportKpiCard'
import { ReportShell } from '@/components/relatorios/ReportShell'
import { ReportTable } from '@/components/relatorios/ReportTable'
import { exportCsv, formatPercent } from '@/components/relatorios/reportUtils'
import { useSellerSalesReport } from '@/hooks/reports/reportHooks'
import { useReportDateRange } from '@/hooks/reports/useReportDateRange'
import { useCurrency } from '@/hooks/useCurrency'

export default function VendasVendedorReport() {
  const { t } = useTranslation('reports')
  const { formatCurrency } = useCurrency()
  const [range, setRange] = useReportDateRange()
  const report = useSellerSalesReport(range)
  const totalWon = report.data.reduce((sum, row) => sum + row.won, 0)
  const totalRevenue = report.data.reduce((sum, row) => sum + row.revenue, 0)

  return (
    <ReportShell
      title={t('seller.title')}
      description={t('seller.description')}
      {...range}
      onRangeChange={setRange}
      loading={report.loading}
      error={report.error}
      empty={!report.data.some((row) => row.leads + row.opportunities + row.won + row.lost > 0)}
      onRetry={report.refetch}
      onExport={() => exportCsv('vendas-vendedor', report.data, [
        { label: t('fields.seller'), value: (row) => row.seller },
        { label: t('fields.leads'), value: (row) => row.leads },
        { label: t('fields.opportunities'), value: (row) => row.opportunities },
        { label: t('fields.won'), value: (row) => row.won },
        { label: t('fields.lost'), value: (row) => row.lost },
        { label: t('fields.revenue'), value: (row) => row.revenue.toFixed(2).replace('.', ',') },
        { label: t('fields.avgTicket'), value: (row) => row.averageTicket.toFixed(2).replace('.', ',') },
        { label: t('fields.conversionPct'), value: (row) => row.conversion.toFixed(1).replace('.', ',') },
      ])}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <ReportKpiCard label={t('seller.kpiWon')} value={totalWon} />
        <ReportKpiCard label={t('fields.revenue')} value={formatCurrency(totalRevenue)} />
        <ReportKpiCard label={t('seller.kpiAvgTicket')} value={formatCurrency(totalWon ? totalRevenue / totalWon : 0)} />
      </div>
      <ReportChart
        type="bar"
        data={report.data.map((row) => ({ ...row }))}
        xKey="seller"
        series={[
          { key: 'won', label: t('fields.won'), color: 'var(--chart-2)' },
          { key: 'lost', label: t('fields.lost'), color: 'var(--chart-5)' },
        ]}
      />
      <ReportTable
        rows={report.data}
        rowKey={(row) => row.sellerId}
        columns={[
          { key: 'seller', label: t('fields.seller'), render: (row) => <span>{row.seller}{!row.active && <small className="ml-2 text-muted-foreground">{t('fields.inactive')}</small>}</span> },
          { key: 'leads', label: t('fields.leads'), align: 'right', render: (row) => row.leads },
          { key: 'opportunities', label: t('fields.opportunities'), align: 'right', render: (row) => row.opportunities },
          { key: 'won', label: t('fields.won'), align: 'right', render: (row) => row.won },
          { key: 'lost', label: t('fields.lost'), align: 'right', render: (row) => row.lost },
          { key: 'revenue', label: t('fields.revenue'), align: 'right', render: (row) => formatCurrency(row.revenue) },
          { key: 'ticket', label: t('fields.avgTicket'), align: 'right', render: (row) => formatCurrency(row.averageTicket) },
          { key: 'conversion', label: t('fields.conversion'), align: 'right', render: (row) => formatPercent(row.conversion) },
        ]}
      />
    </ReportShell>
  )
}
